import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { getPageByPath, buildPageJsonLd } from '../lib/site';

const page = getPageByPath('/metodologia-da-calculadora');

export default function MetodologiaDaCalculadora() {
  useSEO({ title: page.title, description: page.description, path: page.path });

  return (
    <main className="public-reading max-w-[860px] mx-auto px-4 py-8 md:py-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPageJsonLd(page)) }} />
      <nav aria-label="Trilha de navegação" className="mb-8 text-sm text-[var(--text-muted)]">
        <Link to="/">Mini Mental</Link> <span aria-hidden="true">›</span> <Link to="/guias">Guias</Link> <span aria-hidden="true">›</span> Metodologia da calculadora
      </nav>

      <h1 className="text-[var(--primary)] leading-tight mb-6">Como funciona a calculadora do MiniMental.com.br</h1>
      <p className="text-lg text-[var(--text-muted)] leading-relaxed mb-12">
        O MiniMental.com.br é uma ferramenta independente de aplicação guiada, soma e contextualização do MEEM. Não é o formulário oficial do MMSE, não é afiliado ou endossado pela PAR, Inc. e não substitui os canais oficiais de aquisição ou licenciamento do instrumento.
      </p>

      <div className="space-y-12">
        <section>
          <h2 className="text-2xl font-bold mb-4">O que a calculadora faz</h2>
          <p className="text-[var(--text-muted)] leading-relaxed">A ferramenta organiza a pontuação, calcula o total de 0 a 30, mostra subtotais e contextualiza o resultado com referências brasileiras selecionadas. O cálculo acontece no navegador e a pontuação não é enviada ao servidor.</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">De onde vêm as referências brasileiras</h2>
          <div className="space-y-5 text-[var(--text-muted)] leading-relaxed">
            <p><strong>Brucki et al. (2003).</strong> Avaliaram 433 indivíduos saudáveis sem queixas de memória e propuseram regras para uso uniforme do MEEM no Brasil. A escolaridade foi o principal fator associado ao desempenho. As medianas por escolaridade usadas no site são valores descritivos observados nessa amostra: <strong>mediana não é ponto de corte diagnóstico</strong>.</p>
            <p><strong>Bertolucci et al. (1994).</strong> Avaliaram 530 indivíduos e estabeleceram, naquele estudo, valores de corte por escolaridade a partir do percentil inferior da distribuição. Esses valores pertencem ao desenho e à população do estudo; não são limiares universais.</p>
            <p><strong>Lourenço e Veras (2006).</strong> Estudaram o desempenho diagnóstico do MEEM em pessoas com mais de 65 anos. A calculadora identifica essa referência como dependente da população estudada e não a aplica como regra universal.</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">O que o resultado não significa</h2>
          <p className="text-[var(--text-muted)] leading-relaxed">Um escore não diagnostica Alzheimer, demência ou qualquer outra doença. Resultado, escolaridade, funcionalidade, condições de aplicação, sintomas e evolução precisam ser integrados. A calculadora apresenta contexto; não transforma um número em diagnóstico.</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Instrumento oficial e licenciamento</h2>
          <p className="text-[var(--text-muted)] leading-relaxed mb-4">A PAR publica o MMSE e lista <em>Brazilian Portuguese</em> entre as traduções autorizadas. A própria editora orienta que versões modificadas, inclusive alterações de formato ou tradução, passem por seu processo de permissão. O MiniMental.com.br mantém essa camada separada da literatura científica brasileira que usa para contextualização.</p>
          <Link to="/formulario-e-direitos-de-uso" className="inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Ver formulário, direitos de uso e canais oficiais →</Link>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Princípios editoriais</h2>
          <ul className="list-disc pl-6 space-y-2 text-[var(--text-muted)]">
            <li>todo número clínico deve ter fonte, população e finalidade identificáveis;</li>
            <li>medianas não são apresentadas como pontos de corte;</li>
            <li>nenhum escore é apresentado como diagnóstico;</li>
            <li>referências de populações específicas são tratadas como específicas;</li>
            <li>o site não se apresenta como produto oficial, afiliado ou endossado pela PAR.</li>
          </ul>
        </section>
      </div>

      <div className="mt-14 p-6 bg-[var(--surface-soft)] rounded-2xl flex flex-wrap gap-4">
        <Link to="/evidencia-brasileira" className="inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Evidência brasileira →</Link>
        <Link to="/#calculadora" className="inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Voltar à calculadora →</Link>
      </div>
    </main>
  );
}
